import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apollo } from "@/lib/apollo";
import { useSkillsRequestContext } from "@/services/skills";
import type {
  ScheduleFrequency,
  ScheduleListResponse,
  ScheduleMutationResponse,
  SchedulePayload,
  ScheduleRecord,
  ScheduleRunListResponse,
  ScheduleRunRecord,
  ScheduleRunStatus,
  ScheduleTargetType,
} from "@/services/studio/types";
import { LIST_SCHEDULES, CREATE_SCHEDULE, DELETE_SCHEDULE, RUN_SCHEDULE_NOW, UPDATE_SCHEDULE, LIST_SCHEDULE_RUNS } from "@/gql/scheduling";

function normalizeSchedule(raw: Record<string, unknown>): ScheduleRecord {
  return {
    id: String(raw.id),
    org_id: String(raw.orgId ?? ""),
    user_id: (raw.userId ?? null) as string | null,
    target_type: String(raw.targetType ?? "").toLowerCase() as ScheduleTargetType,
    target_id: String(raw.targetId ?? ""),
    target_name: String(raw.targetName ?? ""),
    enabled: Boolean(raw.enabled),
    frequency: String(raw.frequency ?? "").toLowerCase() as ScheduleFrequency,
    time_of_day: String(raw.timeOfDay ?? ""),
    day_of_week: (raw.dayOfWeek ?? null) as number | null,
    day_of_month: (raw.dayOfMonth ?? null) as number | null,
    timezone: String(raw.timezone ?? "UTC"),
    email_recipients: (raw.emailRecipients as string[]) ?? [],
    email_subject: String(raw.emailSubject ?? ""),
    inputs: (raw.inputs ?? null) as Record<string, unknown> | null,
    next_run_at: (raw.nextRunAt ?? null) as string | null,
    last_run_at: (raw.lastRunAt ?? null) as string | null,
    last_run_status: raw.lastRunStatus ? String(raw.lastRunStatus).toLowerCase() : null,
    last_run_error: (raw.lastRunError ?? null) as string | null,
    inserted_at: String(raw.insertedAt ?? ""),
    updated_at: String(raw.updatedAt ?? ""),
  };
}

export function normalizeScheduleRun(raw: Record<string, unknown>): ScheduleRunRecord {
  return {
    id: String(raw.id),
    status: String(raw.status ?? "running").toLowerCase() as ScheduleRunStatus,
    started_at: (raw.startedAt ?? null) as string | null,
    completed_at: (raw.completedAt ?? null) as string | null,
    error_message: (raw.errorMessage ?? null) as string | null,
    result_summary: (raw.resultSummary ?? null) as Record<string, unknown> | null,
    email_sent: Boolean(raw.emailSent),
    email_error: (raw.emailError ?? null) as string | null,
  };
}

function toGqlInput(orgId: string, payload: SchedulePayload): Record<string, unknown> {
  const input: Record<string, unknown> = {
    organizationId: orgId,
    targetType: payload.target_type.toUpperCase(),
  };
  if (payload.target_id) input.targetId = payload.target_id;
  if (payload.frequency) input.frequency = payload.frequency.toUpperCase();
  if (payload.time_of_day) input.timeOfDay = payload.time_of_day;
  if (payload.day_of_week != null) input.dayOfWeek = payload.day_of_week;
  if (payload.day_of_month != null) input.dayOfMonth = payload.day_of_month;
  if (payload.timezone) input.timezone = payload.timezone;
  if (payload.email_recipients?.length) input.emailRecipients = payload.email_recipients;
  if (payload.email_subject) input.emailSubject = payload.email_subject;
  if (payload.inputs) input.inputs = payload.inputs;
  input.enabled = payload.enabled ?? true;
  return input;
}

export function useSchedules() {
  const ctx = useSkillsRequestContext();
  return useQuery<ScheduleListResponse>({
    queryKey: ["schedules", ctx.orgId],
    queryFn: async () => {
      const { data } = await apollo.query<{ listSchedules: Record<string, unknown>[] }>({
        query: LIST_SCHEDULES,
        variables: { organizationId: ctx.orgId },
        fetchPolicy: "network-only",
      });
      const schedules = (data?.listSchedules ?? []).map(normalizeSchedule);
      return { schedules };
    },
    enabled: Boolean(ctx.orgId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useScheduleRuns(scheduleId: string) {
  const ctx = useSkillsRequestContext();
  return useQuery<ScheduleRunListResponse>({
    queryKey: ["schedule-runs", ctx.orgId, scheduleId],
    queryFn: async () => {
      const { data } = await apollo.query<{ listScheduleRuns: Record<string, unknown>[] }>({
        query: LIST_SCHEDULE_RUNS,
        variables: { organizationId: ctx.orgId, scheduleId },
        fetchPolicy: "network-only",
      });
      const runs = (data?.listScheduleRuns ?? []).map(normalizeScheduleRun);
      return { runs };
    },
    enabled: Boolean(ctx.orgId) && Boolean(scheduleId),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation<ScheduleMutationResponse, Error, SchedulePayload>({
    mutationFn: async (payload) => {
      if (!ctx.orgId) throw new Error("No organization selected");
      const { data } = await apollo.mutate<{ createSchedule: Record<string, unknown> }>({
        mutation: CREATE_SCHEDULE,
        variables: { input: toGqlInput(ctx.orgId, payload) },
      });
      return { schedule: normalizeSchedule(data!.createSchedule) };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedules"] });
    },
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation<ScheduleMutationResponse, Error, { scheduleId: string } & SchedulePayload>({
    mutationFn: async ({ scheduleId, ...payload }) => {
      const input: Record<string, unknown> = {};
      if (payload.frequency) input.frequency = payload.frequency.toUpperCase();
      if (payload.time_of_day) input.timeOfDay = payload.time_of_day;
      if (payload.day_of_week != null) input.dayOfWeek = payload.day_of_week;
      if (payload.day_of_month != null) input.dayOfMonth = payload.day_of_month;
      if (payload.timezone) input.timezone = payload.timezone;
      if (payload.email_recipients?.length) input.emailRecipients = payload.email_recipients;
      if (payload.email_subject) input.emailSubject = payload.email_subject;
      if (payload.inputs) input.inputs = payload.inputs;
      if (payload.enabled != null) input.enabled = payload.enabled;
      const { data } = await apollo.mutate<{ updateSchedule: Record<string, unknown> }>({
        mutation: UPDATE_SCHEDULE,
        variables: { scheduleId, input },
      });
      return { schedule: normalizeSchedule(data!.updateSchedule) };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedules"] });
    },
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (scheduleId) => {
      await apollo.mutate({
        mutation: DELETE_SCHEDULE,
        variables: { scheduleId },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedules"] });
    },
  });
}

export function useRunScheduleNow() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; status: string }, Error, string>({
    mutationFn: async (scheduleId) => {
      const { data } = await apollo.mutate<{ runScheduleNow: Record<string, unknown> }>({
        mutation: RUN_SCHEDULE_NOW,
        variables: { scheduleId },
      });
      const raw = data!.runScheduleNow;
      return { ok: true, status: raw.lastRunStatus ? String(raw.lastRunStatus).toLowerCase() : "running" };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedules"] });
    },
  });
}
